import * as React from 'react';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { DataLake } from '@graylog/enterprise-api';

import UserNotification from 'util/UserNotification';
import type { DataLakeActionValues, FieldFilters, FieldsOperator } from 'data-lake/Types';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import RetrievalHelp from 'data-lake/RetrievalHelp';
import DataLakeRetrievalActionForm from 'data-lake/components/DataLakeActionModals/DataLakeRetrievalActionForm';
import { toInternalTime } from 'data-lake/logic/Utils';
import useUserDateTime from 'hooks/useUserDateTime';
import { INTERNAL_DATA_LAKE_TYPE } from 'data-lake/Constants';

const onRetrieveInternalMessages = (
  { data_lake_id, stream_ids, inclusion_type, field_filters, operator }: DataLakeActionValues,
  from: string,
  to: string,
) => {
  const request: {
    data_lake_id: string;
    type: string;
    stream_ids: string[];
    inclusion_type: 'DATA_LAKE_ONLY' | 'INDEXER_ONLY' | 'BOTH';
    field_filters: {
      value: Object;
      field_name: string;
    }[];
    operator: 'AND' | 'OR';
  } = {
    type: INTERNAL_DATA_LAKE_TYPE,
    data_lake_id,
    stream_ids,
    inclusion_type,
    field_filters,
    operator,
  };

  return DataLake.retrieveBulkRetrievalRequest(request, from, to);
};

type Props = {
  archiveStreamId?: string;
  calculateEstimatedSize?: boolean;
  onToggleModal: () => void;
  defaultFieldFilters?: Array<FieldFilters>;
  defaultFieldOperator?: FieldsOperator;
  defaultTimeRange?: AbsoluteTimeRange;
  onRetrieveMessages?: (values: DataLakeActionValues, from: string, to: string) => Promise<unknown>;
};

const RetrievalArchiveModal = ({
  archiveStreamId = undefined,
  calculateEstimatedSize = undefined,
  onToggleModal,
  defaultFieldFilters = undefined,
  defaultFieldOperator = undefined,
  defaultTimeRange = undefined,
  onRetrieveMessages = onRetrieveInternalMessages,
}: Props) => {
  const { selectedDataLake } = useSelectedDataLake();
  const queryClient = useQueryClient();
  const { userTimezone } = useUserDateTime();

  const onRestoreSubmit = useCallback(
    (values: DataLakeActionValues) => {
      const internalFrom = values.from ? toInternalTime(values.from, userTimezone) : '';
      const internalTo = values.to ? toInternalTime(values.to, userTimezone) : '';

      onRetrieveMessages(values, internalFrom, internalTo)
        .then(() => {
          UserNotification.success(`Data Lake was submitted successfully.`, 'Success');
        })
        .catch((error) => {
          UserNotification.error(`An error occurred while retrieving data. ${error}`);
        })
        .finally(() => {
          queryClient.invalidateQueries({
            queryKey: ['data-lake'],
          });
          onToggleModal();
        });
    },
    [onRetrieveMessages, onToggleModal, queryClient, userTimezone],
  );

  return (
    <DataLakeRetrievalActionForm
      calculateEstimatedSize={calculateEstimatedSize}
      modalTitle="Retrieving from Data Lake"
      show
      onHide={onToggleModal}
      submitButtonText="Retrieve"
      dataLakeId={selectedDataLake.id}
      streamIds={[archiveStreamId]}
      onSubmit={onRestoreSubmit}
      defaultTimeRange={defaultTimeRange}
      defaultFieldOperator={defaultFieldOperator}
      defaultFieldFilters={defaultFieldFilters}>
      <p>You are about to retrieve data from 1 Data Lake.</p>
      <RetrievalHelp />
    </DataLakeRetrievalActionForm>
  );
};

export default RetrievalArchiveModal;
